import React, { Fragment, SyntheticEvent, memo, useState } from 'react'
import { useAuthContext } from '../providers/auth'
import { useTranslation } from '@app/lib/hooks/useTranslation'
import { UserManager } from '@app/managers'
import {
  ProfileInputCell,
  profileCellSubmitStyle,
  profileSubmitClassHover,
} from '../general/cells/profile-input'
import { User } from '@app/types'
import { formatMoney } from '@app/lib/utils/price-config'
import Trans from 'next-translate/Trans'
import { SwitchToggle } from '../general/switch-toggle'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@app/@/components/ui/alert-dialog'
import { STRIPE_KEY } from '@app/configs'
import { Button } from '../general'

const AnalyticsSettingsViewComponent = ({
  user,
  loading,
  onSubmit,
  analyticsEnabled,
}: {
  analyticsEnabled?: boolean
  user?: User
  loading?: boolean
  onSubmit?(e: SyntheticEvent<HTMLButtonElement>): Promise<void>
}) => {
  const { t, lang } = useTranslation('settings')
  const { account, setAccountType } = useAuthContext()
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [enableConfirmView, setEnableConfirm] = useState<boolean>(false)

  const { activeSubscription, analyticsDisabled } = account ?? {}

  const onSetEnabled = () => setEnableConfirm(true)

  // toggle the analytics visibility on the dashboard
  const onHideAnalyticsEvent = (e: SyntheticEvent<HTMLButtonElement>) => {
    if (e && typeof e?.preventDefault === 'function') {
      e?.preventDefault()
    }
    const nb = !analyticsDisabled
    setAccountType({ ...account, analyticsDisabled: nb })
    UserManager.setAnalyticsVisibility(nb)
  }

  const onEnableEvent = (e: any) => {
    setModalOpen(false)
    onSubmit && onSubmit(e)
    setEnableConfirm(false)
  }

  const submitBtn = !STRIPE_KEY ? (
    <Button className={profileCellSubmitStyle} onClick={onEnableEvent}>
      {t(!STRIPE_KEY ? 'disabled' : 'forms:cancel')}
    </Button>
  ) : !analyticsEnabled ? (
    <AlertDialogTrigger
      className={profileCellSubmitStyle}
      onClick={onSetEnabled}
    >
      {t('enable-pay')}
    </AlertDialogTrigger>
  ) : (
    <AlertDialogTrigger className='hover:text-gray-600 dark:hover:text-gray-400'>
      {t('cancel-feature')}
    </AlertDialogTrigger>
  )

  if (!STRIPE_KEY) {
    return (
      <ProfileInputCell
        title={t('analytics')}
        subTitle={t(
          activeSubscription ? 'analytics-details' : 'analytics-upgrade'
        )}
        disabledWall={!activeSubscription}
        skeletonLoad={!user && loading}
        submitLabel={{
          left: analyticsEnabled,
          disabled: !activeSubscription || loading,
          title:
            analyticsEnabled || !STRIPE_KEY ? null : (
              <Trans
                i18nKey={'settings:optional-feature'}
                values={{ price: formatMoney(lang, 50) }}
                components={[
                  <Fragment key={'option-feature'} />,
                  <b key={'option-feature1'} />,
                ]}
              />
            ),
          submit: submitBtn,
          submitClassName:
            activeSubscription && analyticsEnabled
              ? 'text-blue-700 dark:text-blue-500 border-0 bg-gray-200 dark:bg-gray-900 hover:text-blue-700 dark:hover:text-blue-400 hover:bg-white dark:hover:bg-black hover:border dark:hover:border-gray-600'
              : activeSubscription
                ? profileSubmitClassHover
                : '',
        }}
      >
        <div className='pt-2'>
          <SwitchToggle
            id={'switch-toggle-analytics'}
            title={`${t('analytics')} ${t(
              !analyticsDisabled && analyticsEnabled ? 'enabled' : 'disabled'
            )}`}
            checked={!analyticsDisabled && analyticsEnabled}
            onChange={onHideAnalyticsEvent}
            disabled={!analyticsEnabled}
          />
        </div>
      </ProfileInputCell>
    )
  }

  return (
    <AlertDialog onOpenChange={setModalOpen} open={modalOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t(enableConfirmView ? 'enable-pay' : 'cancel-feature')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t(
              enableConfirmView ? 'enable-pay-confirm' : 'cancel-addon-confirm',
              {
                addon: t('analytics'),
              }
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('go-back')}</AlertDialogCancel>
          <AlertDialogAction onClick={onEnableEvent}>
            {t(enableConfirmView ? 'enable-pay' : 'cancel-feature')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
      <ProfileInputCell
        title={t('analytics')}
        subTitle={t(
          activeSubscription ? 'analytics-details' : 'analytics-upgrade'
        )}
        disabledWall={!activeSubscription}
        skeletonLoad={!user && loading}
        submitLabel={{
          left: analyticsEnabled,
          disabled: !activeSubscription || loading,
          title:
            analyticsEnabled || !STRIPE_KEY ? null : (
              <Trans
                i18nKey={'settings:optional-feature'}
                values={{ price: formatMoney(lang, 50) }}
                components={[
                  <Fragment key={'option-feature'} />,
                  <b key={'option-feature1'} />,
                ]}
              />
            ),
          submit: submitBtn,
          submitClassName:
            activeSubscription && analyticsEnabled
              ? 'text-blue-700 dark:text-blue-500 border-0 bg-gray-200 dark:bg-gray-900 hover:text-blue-700 dark:hover:text-blue-400 hover:bg-white dark:hover:bg-black hover:border dark:hover:border-gray-600'
              : activeSubscription
                ? profileSubmitClassHover
                : '',
        }}
      >
        <div className='pt-2'>
          <SwitchToggle
            id={'switch-toggle-analytics'}
            title={`${t('analytics')} ${t(
              !analyticsDisabled && analyticsEnabled ? 'enabled' : 'disabled'
            )}`}
            checked={!analyticsDisabled && analyticsEnabled}
            onChange={onHideAnalyticsEvent}
            disabled={!analyticsEnabled}
          />
        </div>
      </ProfileInputCell>
    </AlertDialog>
  )
}

export const AnalyticsSettingsView = memo(AnalyticsSettingsViewComponent)
